import {
  BarChart,
  BarChartOption,
  Button,
  ButtonGroup,
  ButtonMode,
  ButtonSize,
  ChartHandle,
  Checkbox,
  Widget,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowResizeListener } from '../../../../hooks/common';
import useRefEventListener from '../../../../hooks/common/useRefEventListener';
import useFetchRiskDistribution from '../../../../hooks/riskProducts/dashboard/useFetchRiskDistribution';
import useFetchWidgetMetrics from '../../../../hooks/riskProducts/dashboard/useFetchWidgetMetrics';
import {
  RiskDistributionGroupBy,
  RiskDistributionMetric,
} from '../../../../services/dtos/riskProducts/riskDistribution.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import { getChartSubHeader } from '../../../../utils/chartUtils';
import { useGlobalModalContext, MODAL_TYPES } from '../../../GlobalModal';
import NoResults from '../../../NoResults';
import MetricsDropdown from '../MetricsDropdown';
import { RISK_DISTRIBUTION_GROUP_BY_LABEL } from './RiskDistribution.constants';
import { getChartConfig, getTransformedData, riskDistributionWidgetNameGet } from './RiskDistribution.logic';
import styles from './RiskDistribution.module.scss';

const cx = classNames.bind(styles);

interface RiskDistributionProps {
  watchListId: string;
}

const RiskDistribution = ({ watchListId }: RiskDistributionProps): ReactElement => {
  const { showModal } = useGlobalModalContext();
  const chartHandle = useRef<ChartHandle>(null);
  const widgetRef = useRef<HTMLElement>(null);
  const [selectedCreditRiskMetric, setSelectedCreditRiskMetric] = useState<RiskDistributionMetric | undefined>();
  const [selectedGroupBy, setSelectedGroupBy] = useState<RiskDistributionGroupBy>(RiskDistributionGroupBy.SECTOR);
  const { data: widgetMetrics } = useFetchWidgetMetrics();
  const [widgetWidth, setWidgetWidth] = useState<number | undefined>();
  const [unratedToggleOn, setUnratedToggleOn] = useState(false);
  const widgetId = WIDGET_ID.RISK_DISTRIBUTION;

  const { data, isLoading, isError, refetch } = useFetchRiskDistribution(
    watchListId,
    { groupBy: selectedGroupBy, metric: selectedCreditRiskMetric, rated: !unratedToggleOn },
    { enabled: !!selectedCreditRiskMetric }
  );

  const chartData = useMemo(() => {
    return getTransformedData(selectedCreditRiskMetric, data);
  }, [selectedCreditRiskMetric, data]);

  const chartOptions: BarChartOption | undefined = useMemo(() => {
    return getChartConfig(chartData, widgetWidth ?? 0);
  }, [chartData, widgetWidth]);

  const onBarClickedRef = useRefEventListener((event) => {
    showModal(MODAL_TYPES.CHART_DRILL_DOWN, {
      title: riskDistributionWidgetNameGet(selectedGroupBy, selectedCreditRiskMetric),
      subHeader: getChartSubHeader(widgetMetrics, selectedCreditRiskMetric, event.seriesName, event.value, widgetId),
      companies: chartData?.series[event.seriesIndex!].data[event.dataIndex],
      groupBy: selectedGroupBy,
    });
  });

  const onChartInitialized = () => {
    chartHandle.current?.chartInstance?.on('click', (event) => onBarClickedRef.current(event));
  };

  useEffect(() => {
    setWidgetWidth(widgetRef.current?.clientWidth);
    return () => {
      chartHandle.current?.chartInstance?.dispose();
    };
  }, []);

  useWindowResizeListener(() => {
    chartHandle.current?.chartInstance?.resize();
    setWidgetWidth(widgetRef.current?.clientWidth);
  });

  const renderGroupByButton = (groupBy: RiskDistributionGroupBy) => (
    <Button
      className={cx({ active: selectedGroupBy === groupBy })}
      mode={ButtonMode.TERTIARY}
      size={ButtonSize.SMALL}
      onClick={() => setSelectedGroupBy(groupBy)}
      dataTestId={groupBy}
    >
      {RISK_DISTRIBUTION_GROUP_BY_LABEL[groupBy]}
    </Button>
  );

  const renderFirstLine = () => {
    return (
      <div className={cx('actions')}>
        <MetricsDropdown
          widgetId={widgetId}
          value={selectedCreditRiskMetric}
          onOptionSelect={(value) => setSelectedCreditRiskMetric(value as RiskDistributionMetric)}
          isLeftAligned
          dataTestId="distribution-metric-select"
        />
        <ButtonGroup>
          {renderGroupByButton(RiskDistributionGroupBy.SECTOR)}
          {renderGroupByButton(RiskDistributionGroupBy.RATING)}
        </ButtonGroup>
      </div>
    );
  };

  const renderContent = () => {
    if (isEmpty(data)) {
      return <NoResults />;
    }
    return <BarChart ref={chartHandle} option={chartOptions} onChartInitialized={onChartInitialized} />;
  };

  return (
    <Widget
      classNames={cx('body')}
      headerTitle={
        <div className={cx('heading')}>
          <span>{riskDistributionWidgetNameGet(selectedGroupBy, selectedCreditRiskMetric)}</span>
          <Checkbox
            className={cx('unrated-entities-toggle')}
            dataTestId="unrated-entities-toggle"
            onClick={() => setUnratedToggleOn(!unratedToggleOn)}
            checked={unratedToggleOn}
            label="Unrated Entities"
            aria-label="Unrated Entities"
            toggle
          />
        </div>
      }
      firstLine={renderFirstLine()}
      hideFirstLineWhenLoading={false}
      isLoading={isLoading}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      dataTestId="risk-distribution-widget"
      ref={widgetRef}
    >
      {renderContent()}
    </Widget>
  );
};

export default RiskDistribution;
